import { useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  CaretRightIcon,
  KeyIcon,
  LockIcon,
  PlusIcon,
  ShieldCheckIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { SearchBar } from '@/components/ui/search-bar';
import { Skeleton } from '@/components/ui/skeleton';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { ManageRolesContent } from '@/content/ManageRolesContent';
import { useRoles } from '@/lib/hooks/useRoles';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import type { Role } from '@/lib/types/database';
import type { ManageRolesProps } from '@/lib/types/roles.types';
import {
  filterRoles,
  getRoleColorTone,
  sortRolesForDisplay,
} from '@/lib/utils/roles.utils';
import { cn } from '@/lib/utils';

export function ManageRoles({ deletedRoleName }: ManageRolesProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const { data: roles = [], isLoading, error } = useRoles();
  const { toast, showToast, hideToast } = useToastNotification();

  useEffect(() => {
    if (!deletedRoleName) {
      return;
    }

    showToast({
      title: ManageRolesContent.toast.deleteSuccessTitle,
      description:
        ManageRolesContent.toast.deleteSuccessDescription(deletedRoleName),
      variant: 'success',
    });
    navigate({ to: '/admin/manage-roles', replace: true });
  }, [deletedRoleName, navigate, showToast]);

  const visibleRoles = useMemo(
    () => sortRolesForDisplay(filterRoles(roles, searchQuery)),
    [roles, searchQuery]
  );

  const navigateToCreate = () => {
    navigate({ to: '/admin/manage-roles/new' });
  };

  const navigateToDetail = (role: Role) => {
    navigate({
      to: '/admin/manage-roles/$roleId',
      params: { roleId: role.id },
    });
  };

  return (
    <div className="bg-slate-700">
      <div className="mx-auto flex w-full max-w-4xl flex-col px-4 pb-8 pt-3">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="font-field-74 text-h1 leading-none text-slate-50">
              {ManageRolesContent.pageTitle}
            </h1>
            <p className="mt-2 max-w-xl text-body text-slate-400">
              {ManageRolesContent.pageDescription}
            </p>
          </div>
          <Button
            size="sm"
            className="mt-1 shrink-0 bg-purple-300 text-slate-700 hover:bg-purple-400"
            onClick={navigateToCreate}
            aria-label={ManageRolesContent.actions.create}
          >
            <PlusIcon size={20} weight="bold" />
            <span className="hidden sm:inline">
              {ManageRolesContent.actions.create}
            </span>
            <span className="sm:hidden">Create</span>
          </Button>
        </div>

        <div className="mb-4">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={ManageRolesContent.search.placeholder}
            className="[&_input]:border-slate-500 [&_input]:bg-slate-600 [&_input]:text-slate-50 [&_input]:placeholder:text-slate-500 [&_input]:focus-visible:border-purple-300"
          />
        </div>

        {!isLoading && (
          <div className="mb-3 text-small text-slate-500">
            {ManageRolesContent.counts.showing(
              visibleRoles.length,
              roles.length
            )}
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-400 bg-red-500/10 px-4 py-3 text-body text-red-100">
            {error instanceof Error ? error.message : 'Failed to load roles.'}
          </div>
        )}

        {isLoading ? (
          <div className="space-y-2">
            {[...Array(4)].map((_, index) => (
              <Skeleton
                key={index}
                className="h-16 w-full rounded-lg bg-slate-600"
              />
            ))}
          </div>
        ) : visibleRoles.length === 0 ? (
          <div className="rounded-lg border border-slate-500 bg-slate-600 px-4 py-10 text-center">
            <ShieldCheckIcon
              size={32}
              weight="duotone"
              className="mx-auto mb-3 text-purple-300"
            />
            <p className="text-large text-slate-50">
              {roles.length === 0
                ? ManageRolesContent.emptyStates.noRoles
                : ManageRolesContent.emptyStates.noFilteredRoles}
            </p>
            {roles.length === 0 && (
              <p className="mx-auto mt-2 max-w-md text-body text-slate-300">
                {ManageRolesContent.emptyStates.noRolesDescription}
              </p>
            )}
            {roles.length === 0 && (
              <Button className="mt-5" onClick={navigateToCreate}>
                <PlusIcon size={20} weight="bold" />
                {ManageRolesContent.actions.create}
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg bg-slate-600">
            {visibleRoles.map((role, index) => (
              <RoleListRow
                key={role.id}
                role={role}
                hasDivider={index > 0}
                onNavigateToDetail={navigateToDetail}
              />
            ))}
          </div>
        )}
      </div>
      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </div>
  );
}

interface RoleListRowProps {
  role: Role;
  hasDivider: boolean;
  onNavigateToDetail: (role: Role) => void;
}

function RoleListRow({
  role,
  hasDivider,
  onNavigateToDetail,
}: RoleListRowProps) {
  const tone = getRoleColorTone(role.chip_color);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onNavigateToDetail(role);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="role-row"
      aria-label={role.display_name}
      className={cn(
        'flex cursor-pointer items-center gap-3 px-3 py-4 text-left transition-colors hover:bg-slate-500/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 focus-visible:ring-inset sm:px-4',
        hasDivider && 'border-t border-slate-550 border-opacity-5'
      )}
      onClick={() => onNavigateToDetail(role)}
      onKeyDown={handleKeyDown}
    >
      <span
        className={cn('h-2.5 w-2.5 shrink-0 rounded-full', tone.dotClassName)}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-large text-slate-50">
            {role.display_name}
          </span>
          {role.is_system && (
            <span className="flex shrink-0 items-center gap-1 rounded-full border border-blue-400/40 bg-blue-400/15 px-1.5 py-0.5 font-field-42 text-[9px] uppercase text-blue-200">
              <LockIcon size={9} weight="bold" />
              {ManageRolesContent.badges.system}
            </span>
          )}
        </div>
        <div className="mt-1 flex min-w-0 items-center gap-2 text-small text-slate-500">
          <KeyIcon size={13} weight="bold" className="shrink-0" />
          <code className="truncate font-mono text-[12px] text-slate-400">
            {role.key}
          </code>
        </div>
      </div>
      <CaretRightIcon
        size={16}
        weight="bold"
        className="shrink-0 text-slate-500"
      />
    </div>
  );
}
